import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  ListSubheader,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Inbox as EmptyIcon,
  Route as RouteIcon,
} from '@mui/icons-material';

const Reservas = () => {
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todas');
  const [estadisticas, setEstadisticas] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Datos para selects
  const [rutas, setRutas] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [guiasDisponibles, setGuiasDisponibles] = useState([]);
  const [guiasReservadas, setGuiasReservadas] = useState([]);
  const [loadingGuias, setLoadingGuias] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    ruta_id: '',
    agente_id: '',
    guia_id: '',
    kilos: '',
  });

  // Ruta seleccionada
  const [selectedRuta, setSelectedRuta] = useState(null);

  useEffect(() => {
    fetchData();
    fetchSelects();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);

      let url = `${endpoints.reservas}?limit=100`;
      if (filter === 'activas') url += '&activa=1';
      if (filter === 'pendientes') url += '&activa=0';

      const [reservasRes, statsRes] = await Promise.all([
        api.get(url),
        api.get(`${endpoints.reservas}?estadisticas=1`),
      ]);

      setReservas(reservasRes.data.data.reservas || []);
      setEstadisticas(statsRes.data.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSelects = async () => {
    try {
      const [rutasRes, agentesRes] = await Promise.all([
        api.get(endpoints.rutas.list),
        api.get(endpoints.oficina.agentes),
      ]);
      setRutas(rutasRes.data.data?.rutas || []);
      setAgentes(agentesRes.data.data || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    }
  };

  const fetchGuiasByAgente = useCallback(async (agenteId) => {
    if (!agenteId) {
      setGuiasDisponibles([]);
      setGuiasReservadas([]);
      return;
    }

    try {
      setLoadingGuias(true);
      const res = await api.get(`${endpoints.guias.porAgente}?agente_id=${agenteId}`);
      setGuiasDisponibles(res.data.data?.disponibles || []);
      setGuiasReservadas(res.data.data?.reservadas || []);
    } catch (error) {
      console.error('Error cargando guías:', error);
      setGuiasDisponibles([]);
      setGuiasReservadas([]);
    } finally {
      setLoadingGuias(false);
    }
  }, []);

  const handleAdd = () => {
    setFormData({
      fecha: new Date().toISOString().split('T')[0],
      ruta_id: '',
      agente_id: '',
      guia_id: '',
      kilos: '',
    });
    setSelectedRuta(null);
    setGuiasDisponibles([]);
    setGuiasReservadas([]);
    setError('');
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'ruta_id') {
      const ruta = rutas.find((r) => r.id == value);
      setSelectedRuta(ruta || null);
    }

    if (name === 'agente_id') {
      setFormData((prev) => ({ ...prev, guia_id: '' }));
      fetchGuiasByAgente(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.ruta_id) {
      setError('Seleccione una ruta');
      return;
    }
    if (!formData.agente_id) {
      setError('Seleccione un agente');
      return;
    }
    if (!formData.guia_id) {
      setError('Seleccione una guía');
      return;
    }

    setSaving(true);

    try {
      await api.post(endpoints.reservas, {
        fecha: formData.fecha,
        ruta_id: parseInt(formData.ruta_id),
        agente_id: parseInt(formData.agente_id),
        guia_id: parseInt(formData.guia_id),
        kilos: parseInt(formData.kilos) || 0,
      });
      setShowModal(false);
      fetchData();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Está seguro de eliminar esta reserva?')) return;

    try {
      await api.delete(endpoints.reservas, { data: { id } });
      fetchData();
    } catch (error) {
      alert('Error al eliminar: ' + (error.response?.data?.message || error.message));
    }
  };

  // Agrupar reservas por vuelo para subtotales
  const reservasPorVuelo = reservas.reduce((acc, r) => {
    if (!acc[r.cVuelo]) {
      acc[r.cVuelo] = { reservas: [], totalKilos: 0 };
    }
    acc[r.cVuelo].reservas.push(r);
    acc[r.cVuelo].totalKilos += r.kilos || 0;
    return acc;
  }, {});

  const StatCard = ({ value, label, color }) => (
    <Card sx={{ borderLeft: 4, borderColor: `${color}.main` }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Reservas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestión de reservas de carga
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={handleAdd}>
            Nueva Reserva
          </Button>
          <Button component={Link} to="/oficina" variant="contained" color="inherit" startIcon={<BackIcon />}>
            Volver
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      {estadisticas && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatCard value={estadisticas.total} label="Total Reservas" color="primary" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard value={estadisticas.activas} label="Activas" color="success" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard value={estadisticas.voladas} label="Voladas" color="secondary" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              value={`${(estadisticas.total_kilos || 0).toLocaleString()} kg`}
              label="Total Kilos"
              color="warning"
            />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(e, value) => value && setFilter(value)}
          size="small"
        >
          <ToggleButton value="todas">Todas</ToggleButton>
          <ToggleButton value="pendientes">Pendientes</ToggleButton>
          <ToggleButton value="activas">Activas</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Table por vuelo con subtotales */}
      {Object.keys(reservasPorVuelo).length === 0 ? (
        <Card>
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <EmptyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">No hay reservas para mostrar</Typography>
          </Box>
        </Card>
      ) : (
        Object.entries(reservasPorVuelo).map(([vuelo, data]) => (
          <Card key={vuelo} sx={{ mb: 3 }}>
            <CardContent sx={{ pb: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <RouteIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Vuelo: {vuelo}
                </Typography>
                <Chip
                  label={`${data.reservas.length} reserva(s)`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Destino</TableCell>
                    <TableCell align="right">Kilos</TableCell>
                    <TableCell>Guía</TableCell>
                    <TableCell>Agente</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.reservas.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{new Date(r.fecha).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip label={r.dest} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{(r.kilos || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {r.guia}
                        </Typography>
                      </TableCell>
                      <TableCell>{r.agente}</TableCell>
                      <TableCell align="center">
                        {r.volada ? (
                          <Chip label="Volada" size="small" color="success" />
                        ) : r.activa ? (
                          <Chip label="Activa" size="small" color="primary" />
                        ) : (
                          <Chip label="Pendiente" size="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton color="error" size="small" onClick={() => handleDelete(r.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Subtotal */}
                  <TableRow sx={{ bgcolor: 'warning.50' }}>
                    <TableCell colSpan={2}>
                      <Typography fontWeight="bold">Subtotal</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{data.totalKilos.toLocaleString()}</Typography>
                    </TableCell>
                    <TableCell colSpan={4} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        ))
      )}

      {/* Gran Total */}
      {reservas.length > 0 && (
        <Card sx={{ bgcolor: 'primary.50' }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" color="primary">
              GRAN TOTAL: {reservas.reduce((sum, r) => sum + (r.kilos || 0), 0).toLocaleString()} Kilos
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Modal Nueva Reserva */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Nueva Reserva
          <IconButton onClick={() => setShowModal(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* Ruta */}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Ruta</InputLabel>
                  <Select name="ruta_id" value={formData.ruta_id} onChange={handleInputChange} label="Ruta">
                    <MenuItem value="">Seleccione una ruta</MenuItem>
                    {rutas.map((ruta) => (
                      <MenuItem key={ruta.id} value={ruta.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={ruta.code} size="small" color="primary" />
                          <span>{ruta.origen} → {ruta.destino}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Preview ruta */}
              {selectedRuta && (
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                    <Typography variant="body2" color="success.dark">
                      <strong>{selectedRuta.code}:</strong> {selectedRuta.ruta_visual || `${selectedRuta.origen} → ${selectedRuta.destino}`}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Agente */}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Agente</InputLabel>
                  <Select name="agente_id" value={formData.agente_id} onChange={handleInputChange} label="Agente">
                    <MenuItem value="">Seleccione un agente</MenuItem>
                    {agentes.map((ag) => (
                      <MenuItem key={ag.id} value={ag.id}>
                        {ag.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Guía */}
              <Grid item xs={12}>
                <FormControl fullWidth required disabled={!formData.agente_id || loadingGuias}>
                  <InputLabel>Guía</InputLabel>
                  <Select name="guia_id" value={formData.guia_id} onChange={handleInputChange} label="Guía">
                    <MenuItem value="">
                      {loadingGuias ? 'Cargando...' : formData.agente_id ? 'Seleccione una guía' : 'Primero seleccione un agente'}
                    </MenuItem>
                    {guiasReservadas.length > 0 && (
                      <ListSubheader>Pre-Asignadas</ListSubheader>
                    )}
                    {guiasReservadas.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.guia} {g.para && `- ${g.para}`}
                      </MenuItem>
                    ))}
                    {guiasDisponibles.length > 0 && (
                      <ListSubheader>Disponibles</ListSubheader>
                    )}
                    {guiasDisponibles.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.guia}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Fecha"
                  type="date"
                  name="fecha"
                  value={formData.fecha}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Kilos"
                  type="number"
                  name="kilos"
                  value={formData.kilos}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="0"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Guardar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Reservas;
